import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { ItemModel } from '@backbase/foundation-ang/core';
import {
  CapabilityData,
  ChallengeError,
  ChallengeResponse,
  ChallengeResponseFunction,
  TransactionSigningHandler,
  TransactionSigningService,
} from '../../identity-core-ang/public_api';
import { BehaviorSubject, EMPTY, from, Observable, of, Subscription, throwError, timer } from 'rxjs';
import { catchError, first, retryWhen, switchMap } from 'rxjs/operators';

@Component({
  selector: 'bb-transaction-signing-widget',
  templateUrl: 'transaction-signing-widget.component.html',
})
export class TransactionSigningWidgetComponent implements OnDestroy, OnInit {
  private readonly errorSubject = new BehaviorSubject<ChallengeError | undefined>(undefined);
  private readonly pendingSubject = new BehaviorSubject<boolean>(false);
  private readonly challengeSubject = new BehaviorSubject<ChallengeResponse | undefined>(undefined);
  private postChallengeResponse: ChallengeResponseFunction | undefined;
  private postFailureToWidget: Function | undefined;
  private postSuccessToWidget: Function | undefined;

  private readonly requestSubscription = this.setupRequestSubscription();
  private pollingSubscription: Subscription | undefined;
  readonly challenge = this.challengeSubject.asObservable();
  readonly error = this.errorSubject.asObservable();
  readonly pending = this.pendingSubject.asObservable();
  txnData: CapabilityData | undefined;

  enableResendPushNotification: Observable<boolean> = undefined;

  constructor(
    private readonly transactionSigningService: TransactionSigningService,
    private readonly itemModel: ItemModel,
  ) {
    console.log('itemModeil', itemModel);
    
    this.enableResendPushNotification = this.itemModel!.property<boolean>(
      'enableResendPushNotification',
      false,
    );
  }

  ngOnInit(): void {
    //this.challenge.subscribe((data) => console.log('Challenge -> ', data));
  }

  ngOnDestroy(): void {
    this.requestSubscription.unsubscribe();
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  onFailure(error: Error) {
    if (this.postFailureToWidget) {
      this.postFailureToWidget(error, this.txnData);
      this.clearChallenge();
    }
  }

  onSuccess() {
    if (this.postSuccessToWidget) {
      this.postSuccessToWidget(this.txnData);
      this.clearChallenge();
    }
  }

  onPoll(data: object) {
    this.pollingSubscription = of(data)
      .pipe(
        switchMap((d) => this.performChallenge(d)),
        first(),
        retryWhen((errors: Observable<ChallengeResponse>) => {
          return errors.pipe(
            switchMap((response: ChallengeResponse) => {
              if (response.error) {
                return throwError(response);
              } else {
                this.postChallengeResponse = response.postChallengeResponse;
              }
              return this.getPollingCycleTimePreference().pipe(switchMap((time) => timer(time)));
            }),
          );
        }),
        catchError((response: ChallengeResponse) => {
          if (this.postChallengeResponse) {
            this.errorSubject.next(response.error);
          }
          return EMPTY;
        }),
      )
      .subscribe(() => this.onSuccess());
  }

  onPost(data: object) {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
    this.pendingSubject.next(true);
    this.performChallenge(data)
      .pipe(
        first(),
        catchError((response: ChallengeResponse) => {
          if (response.error) {
            if (response.error.errorDescription === 'rejected_by_user') {
              this.onFailure(new Error(response.error.errorDescription));
            } else {
              this.errorSubject.next(response.error);
            }
          } else {
            this.postChallengeResponse = response.postChallengeResponse;
            this.challengeSubject.next(response);
          }
          this.pendingSubject.next(false);
          return EMPTY;
        }),
      )
      .subscribe(() => this.onSuccess());
  }

  private performChallenge(data: object): Observable<ChallengeResponse> {
    if (!this.postChallengeResponse) {
      return throwError({ error: {} });
    }

    return from(this.postChallengeResponse(data)) as Observable<ChallengeResponse>;
  }

  private setupRequestSubscription() {
    return this.transactionSigningService.requestSubject.subscribe((value) => this.updateChallenge(value));
  }

  private updateChallenge(handler: TransactionSigningHandler) {
    this.postFailureToWidget = handler.failure;
    this.postSuccessToWidget = handler.success;
    this.txnData = handler.transactionData;
    this.pendingSubject.next(false);
    if (handler.challenge) {
      this.challengeSubject.next(handler.challenge);
      this.postChallengeResponse = handler.challenge.postChallengeResponse;
    }
  }

  private clearChallenge() {
    this.postChallengeResponse = undefined;
    this.postFailureToWidget = undefined;
    this.postSuccessToWidget = undefined;
    this.challengeSubject.next(undefined);
    this.errorSubject.next(undefined);
    this.txnData = undefined;
    this.pendingSubject.next(false);
  }

  private getPollingCycleTimePreference(): Observable<number> {
    return this.itemModel!.property<number>('pollingCycleTime', 3000);
  }
}
